import { css } from '@emotion/react';
import { useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';

// Schemas
import { MinimalActivity } from 'redux/schemas/models/activity';

// Selectors
import { getCourseActivities } from 'redux/selectors/course';

// Components
import { NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';

// Styles
import { info, gray2, gray4, gray7, gray6 } from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { textLargeFontSize } from 'styles/global_defaults/fonts';
import { liveSessionsActivities } from '../../communication-item/communication-filter-row';

export type LiveSessionWithSubSessions = MinimalActivity & {
  sessions?: {
    id: number;
    title: string;
  }[];
};

/**
 * Creates the shared styles for live event filter components.
 * @param className - The unique class name for the component (e.g., 'live-event-registration-trigger')
 */
export const createLiveEventFilterStyles = (className: string) => css`
  &.${className} {
    background-color: ${gray7};
    border-radius: 10px;

    .selected-values {
      display: inline-block;
      margin-left: ${halfSpacing}px;
    }

    .dropdowns {
      visibility: hidden;
      display: flex;
      height: 0;

      .live-event {
        flex: 1;
        .title {
          font-size: ${textLargeFontSize}px;
        }
      }

      .session {
        flex: 3;
      }

      .live-event, .session {
        .title {
          font-size: ${textLargeFontSize}px;
        }
      }
    }

    .info {
      flex: 1 0 auto;
      text-align: right;
      color: ${gray2};
      vertical-align: middle;
      line-height: 24px;
    }

    &.on {
      background-color: ${info};

      .dropdowns {
        visibility: visible;
        height: auto;
        margin-bottom: ${halfSpacing}px;
      }
    }

    &.disabled {
      background-color: ${gray6};
      display: flex;

      .info {
        color: ${gray4};
        display: inherit;
        justify-content: flex-end;
        align-items: center;
      }

      .dropdowns {
        display: none;
      }
    }
  }
`;

/**
 * Gets the ID value from a form field value (handles both {value: id} and {id: id} formats)
 */
export const getFormValueId = (formValue: { value?: number; id?: number } | null | undefined): number | undefined => {
  if (!formValue) return undefined;
  return formValue.value ?? formValue.id;
};

interface UseLiveEventFilterOptions {
  /** Field names to clear when the filter is disabled */
  fieldsToClear: string[];
  /** Whether to filter to only multi-session events (default: true) */
  multiSessionOnly?: boolean;
}

interface UseLiveEventFilterResult {
  /** List of live events/sessions from course activities */
  liveEventsList: LiveSessionWithSubSessions[];
  /** Whether there are no live events available */
  noLiveEvents: boolean;
  /** Form context setValue function */
  setValue: ReturnType<typeof useFormContext>['setValue'];
  /** Form context watch function */
  watch: ReturnType<typeof useFormContext>['watch'];
  /** Clear specified form fields */
  clearFields: (fields: string[]) => void;
  /** Build dropdown options from a list of activities */
  buildDropdownOptions: (
    activities: MinimalActivity[],
    onSelect?: (activity: MinimalActivity) => void
  ) => NvDropdownTextItem[];
}

/**
 * Shared hook for live event filter components.
 * Provides common functionality for fetching live events, building dropdown options,
 * and clearing form fields.
 */
export const useLiveEventFilter = (
  isEnabled: boolean,
  options: UseLiveEventFilterOptions
): UseLiveEventFilterResult => {
  const { setValue, watch } = useFormContext();
  const courseActivities = useSelector((state) => getCourseActivities(state));
  const { fieldsToClear, multiSessionOnly = true } = options;

  // Get live events list (optionally filtered to multi-session only)
  const liveEventsList: LiveSessionWithSubSessions[] = useMemo(() => {
    const allSessions = liveSessionsActivities(courseActivities);
    if (!multiSessionOnly) return allSessions;
    return allSessions.filter(
      (liveSession: LiveSessionWithSubSessions) => (liveSession.sessions?.length ?? 0) > 1
    );
  }, [courseActivities, multiSessionOnly]);

  const noLiveEvents = liveEventsList.length === 0;

  // Clear fields helper
  const clearFields = useCallback(
    (fields: string[]) => {
      fields.forEach((field) => {
        setValue(field, null, { shouldValidate: true, shouldDirty: true });
      });
    },
    [setValue]
  );

  // Build dropdown options from activities
  const buildDropdownOptions = useCallback(
    (activities: MinimalActivity[], onSelect?: (activity: MinimalActivity) => void): NvDropdownTextItem[] => {
      return activities.map((activity) => ({
        id: activity.id,
        type: 'text' as const,
        text: activity.title,
        value: activity.id,
        ...(onSelect && { callback: () => onSelect(activity) }),
      }));
    },
    []
  );

  // Clear fields when disabled
  useEffect(() => {
    if (!isEnabled && fieldsToClear.length > 0) {
      clearFields(fieldsToClear);
    }
  }, [isEnabled, clearFields, fieldsToClear]);

  return {
    liveEventsList,
    noLiveEvents,
    setValue,
    watch,
    clearFields,
    buildDropdownOptions,
  };
};
